<template>
  <div class="team-progress-alert" role="status" :aria-label="ariaLabel">
    <!-- Icon -->
    <div class="icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" class="trophy">
        <path
          d="M6 3h12v2h3a1 1 0 0 1 1 1c0 3.314-2.686 6-6 6h-.341A6.01 6.01 0 0 1 13 14.917V17h3a1 1 0 1 1 0 2H8a1 1 0 1 1 0-2h3v-2.083A6.01 6.01 0 0 1 8.341 12H8c-3.314 0-6-2.686-6-6a1 1 0 0 1 1-1h3V3Zm2 2v2a4 4 0 1 0 8 0V5H8Zm-4 2c.424 1.997 2.18 3.5 4.285 3.5H8a5.98 5.98 0 0 1-2-3.5H4Zm16 0h-2a5.98 5.98 0 0 1-2 3.5h-.285C17.82 10.5 19.576 8.997 20 7Z"
        />
      </svg>
    </div>

    <!-- Headline -->
    <p class="headline-left">
      {{ $t('teams.ready_percent_banner', {
        percent: percent,
        remaining: remainingSteps,
        nextStep: nextStepLabel
      }) }}
    </p>

    <!-- Meta -->
    <div class="meta">
      <span class="progress-label">{{ $t('progress') }}:</span>
      <strong class="progress-value">
        {{ stepsCompleted }} {{ $t('of') }} {{ totalSteps }}
        {{ $t('steps_completed') }}
      </strong>
    </div>

    <!-- Full-width discount section -->
    <div class="discount-container">
      <transition name="fade">
        <p v-if="expanded" key="full" class="discount-text">
          {{ $t('completion_steps.discount_description') }}
        </p>
        <p v-else key="short" class="discount-text truncated">
          {{ shortText }}...
        </p>
      </transition>

      <button
        class="toggle-btn"
        type="button"
        @click="expanded = !expanded"
        :aria-expanded="expanded.toString()"
      >
        {{ expanded ? $t('common.show_less') : $t('common.show_more') }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TeamProgressAlert',
  props: {
    stepsCompleted: { type: Number, required: true },
    totalSteps: { type: Number, required: true },
    nextStepLabel: { type: String, required: true }
  },
  data () {
    return {
      expanded: false
    }
  },
  computed: {
    percent () {
      const p = Math.round((this.stepsCompleted / this.totalSteps) * 100)
      return Math.max(0, Math.min(100, p))
    },
    remainingSteps () {
      return Math.max(this.totalSteps - this.stepsCompleted, 0)
    },
    ariaLabel () {
      return this.$t('teams.ready_percent_banner', {
        percent: this.percent,
        completed: this.stepsCompleted,
        total: this.totalSteps,
        nextStep: this.nextStepLabel
      })
    },
    shortText () {
      const full = this.$t('completion_steps.discount_description')
      return full.slice(0, 110)
    }
  }
}
</script>

<style lang="scss" scoped>
.team-progress-alert {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  gap: 16px;
  width: 100%;
  padding: 16px 20px;
  margin: 24px auto;
  background: #fff4d7; /* soft yellow */
  border-radius: 10px;
  color: #111;
  border-left: 5px solid #f1a300;

  .icon {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    background: rgba(255, 193, 7, 0.25);
    border-radius: 12px;

    .trophy {
      width: 24px;
      height: 24px;
      fill: #f1a300;
    }
  }

  .headline-left {
    margin: 0;
    font-family: AvantGarde-Bold, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 1.3;
  }

  .meta {
    white-space: nowrap;
    font-size: 16px;
    font-family: AvantGarde-Bold, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;

    .progress-label {
      margin-right: 6px;
      opacity: 0.9;
    }

    .progress-value {
      font-weight: 700;
    }
  }

  /* discount section spans full width */
  .discount-container {
    grid-column: 1 / -1; /* span across all columns */
    margin-top: 10px;
    padding-left: 60px; /* indent under content, not icon edge */
    padding-right: 16px;

    .discount-text {
      margin: 0;
      line-height: 1.5;
      font-size: 14px;
      color: #333;
    }

    .truncated {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: normal;
    }

    .toggle-btn {
      margin-top: 8px;
      background: none;
      border: none;
      color: #c77b00;
      font-weight: 600;
      cursor: pointer;
      font-size: 14px;
      padding: 0;
      text-decoration: underline;
      transition: color 0.2s;

      &:hover {
        color: #a86200;
      }
    }
  }
}

/* Fade animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Responsive layout */
@media (max-width: 640px) {
  .team-progress-alert {
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;

    .meta {
      grid-column: 1 / -1;
      margin-top: 6px;
      white-space: normal;
    }

    .discount-container {
      padding-left: 50px;
    }
  }
}
</style>
